import { useMemo, useState } from 'react';
import { setTransactionSplits, type FinanceTransaction } from '../api.js';
import { Button, IconButton, Sheet, useToast } from '../ui/index.js';
import { errorText } from '../lifeos/toast.js';
import { isUncategorized, money } from './format.js';
import { useCategoryTree } from './tree.js';
import './split.css';

/**
 * Split one bank row across categories (migration 0025) — a Sam's Club run
 * that's part groceries, part fronted for a friend. Amounts are typed as
 * positive dollars and carry the row's sign; Save stays disabled until the
 * lines add up to the row to the cent. Exported for Review to reuse.
 */

/**
 * "$1,234.5" → 123450, parsed as a string (`Number('1.005') * 100` is
 * 100.4999…). Null unless it's a plain non-negative amount with ≤ 2 decimals.
 */
export function parseAmountToCents(input: string): number | null {
  const s = input.replace(/[$,\s]/g, '');
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m) return null;
  const whole = m[1] ?? '';
  const frac = m[2] ?? '';
  if (!whole && !frac) return null;
  return Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
}

/** Cents (magnitude) → what the amount input shows: "60", "12.50". */
export function centsToAmount(cents: number): string {
  const c = Math.abs(cents);
  const whole = Math.floor(c / 100);
  const frac = c % 100;
  return frac ? `${whole}.${String(frac).padStart(2, '0')}` : String(whole);
}

interface Draft {
  key: number;
  category: string;
  amount: string;
  note: string;
}

/** Magnitude still to place: positive = remaining, negative = over. Null while any amount is unreadable. */
export function remainingCents(totalCents: number, amounts: string[]): number | null {
  let placed = 0;
  for (const a of amounts) {
    if (!a.trim()) continue;
    const c = parseAmountToCents(a);
    if (c === null) return null;
    placed += c;
  }
  return Math.abs(totalCents) - placed;
}

export default function SplitEditor({
  txn,
  onClose,
  onSaved,
}: {
  txn: FinanceTransaction;
  onClose: () => void;
  onSaved: (t: FinanceTransaction) => void;
}): JSX.Element {
  const idx = useCategoryTree();
  const toast = useToast();
  const inflow = txn.amountCents > 0;
  const total = Math.abs(txn.amountCents);
  const sign = inflow ? 1 : -1;

  const [lines, setLines] = useState<Draft[]>(() =>
    txn.splits?.length
      ? txn.splits.map((l, i) => ({ key: i, category: l.category, amount: centsToAmount(l.amountCents), note: l.note ?? '' }))
      : [
          { key: 0, category: isUncategorized(txn.category) ? '' : (txn.category ?? ''), amount: centsToAmount(total), note: '' },
          { key: 1, category: '', amount: '', note: '' },
        ],
  );
  const [nextKey, setNextKey] = useState(lines.length);
  const [busy, setBusy] = useState(false);

  // Income only for money coming in; never "uncategorized".
  const options = useMemo(
    () =>
      idx.flat.filter((f) => {
        const root = f.path.split('.')[0];
        return root !== 'uncategorized' && (root !== 'income' || inflow);
      }),
    [idx, inflow],
  );

  const left = remainingCents(txn.amountCents, lines.map((l) => l.amount));
  const cents = lines.map((l) => parseAmountToCents(l.amount));
  const valid =
    lines.length >= 2 && left === 0 && lines.every((l, i) => l.category && cents[i] !== null && cents[i]! > 0);

  const patch = (key: number, p: Partial<Draft>): void =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));

  const addLine = (): void => {
    setLines((ls) => [...ls, { key: nextKey, category: '', amount: left && left > 0 ? centsToAmount(left) : '', note: '' }]);
    setNextKey((k) => k + 1);
  };

  const send = async (payload: Parameters<typeof setTransactionSplits>[1]): Promise<void> => {
    setBusy(true);
    try {
      onSaved(await setTransactionSplits(txn.id, payload));
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };

  const save = (): void => {
    if (!valid || busy) return;
    void send(
      lines.map((l, i) => ({ category: l.category, amountCents: sign * cents[i]!, ...(l.note.trim() ? { note: l.note.trim() } : {}) })),
    );
  };

  const status =
    left === null ? 'Check the amounts' : left === 0 ? 'Balanced' : left > 0 ? `Remaining ${money(left)}` : `Over by ${money(-left)}`;

  return (
    <Sheet title="Split" onClose={onClose} action={{ label: 'Save', onClick: save, disabled: !valid || busy }} className="mn-splitsheet">
      <form
        className="hb-form mn-split"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="mn-split__head">
          <span className="mn-split__who">{txn.merchant ?? txn.description}</span>
          <span className={`hb-amount${inflow ? ' hb-amount--in' : ''}`}>{money(txn.amountCents, { sign: inflow })}</span>
        </div>

        <ol className="mn-split__lines">
          {lines.map((l, i) => (
            <li key={l.key} className="mn-split__line">
              <div className="mn-split__row">
                <label className="hb-field mn-split__cat">
                  <span>Line {i + 1}</span>
                  <select
                    aria-label={`Line ${i + 1} category`}
                    value={l.category}
                    onChange={(e) => patch(l.key, { category: e.target.value })}
                  >
                    <option value="">Choose a category…</option>
                    {options.map((o) => (
                      <option key={o.path} value={o.path}>
                        {o.chain.join(' › ')}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="hb-field mn-split__amt">
                  <span>Amount</span>
                  <input
                    inputMode="decimal"
                    placeholder="$0.00"
                    aria-label={`Line ${i + 1} amount`}
                    value={l.amount}
                    onChange={(e) => patch(l.key, { amount: e.target.value })}
                  />
                </label>
                {lines.length > 2 && (
                  <IconButton
                    icon="x"
                    label={`Remove line ${i + 1}`}
                    className="mn-split__drop"
                    onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                  />
                )}
              </div>
              <input
                className="mn-split__note"
                placeholder="Note (optional)"
                aria-label={`Line ${i + 1} note`}
                maxLength={500}
                value={l.note}
                onChange={(e) => patch(l.key, { note: e.target.value })}
              />
              {l.amount && cents[i] === null && <span className="hb-form__error">Enter an amount like 60 or 12.50.</span>}
            </li>
          ))}
        </ol>

        <div className="mn-split__foot">
          <Button variant="secondary" size="sm" icon="plus" onClick={addLine} disabled={lines.length >= 50}>
            Add line
          </Button>
          <span className={`mn-split__left${left === 0 ? ' mn-split__left--ok' : ''}`} role="status">
            {status}
          </span>
        </div>

        <Button type="submit" size="lg" block disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Save split'}
        </Button>
        {txn.splits?.length ? (
          <Button variant="destructive" block disabled={busy} onClick={() => void send([])}>
            Remove split
          </Button>
        ) : null}
      </form>
    </Sheet>
  );
}

/** "Split · 2 categories" — the ledger's sub line for a split row. */
export function splitSummary(t: FinanceTransaction): string | null {
  const n = t.splits?.length ?? 0;
  if (n === 0) return null;
  const cats = new Set(t.splits!.map((l) => l.category)).size;
  return `Split · ${cats} ${cats === 1 ? 'category' : 'categories'}`;
}
